import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

export const JSSpreadRest: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Spread &amp; Rest Operators</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    One syntax, two opposite jobs. Spread expands values out; rest collects them in. Master that single distinction and every spread/rest question becomes easy.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="16 min read" />
  </div>

  {/* 1. Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>An e-commerce app constantly needs to merge catalogs, copy arrays, merge/clone objects, pass variable arguments, and pull out remaining values:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const electronics = ["Laptop", "Phone"];\nconst accessories = ["Mouse", "Keyboard"];\n\n// old way\nconst catalog = electronics.concat(accessories);` }]} defaultLanguage="javascript" id="sr-problem" />
   <Para>ES6 introduced the <Code>...</Code> operator to make all of this expressive — but the <Mark>same</Mark> syntax powers two different concepts, which trips people up: the <Mark>spread</Mark> operator and the <Mark>rest</Mark> operator.</Para>
  </section>

  {/* 2. Definition */}
  <section className="mb-12">
   <SectionHeading n={2}>Definition</SectionHeading>
   <div className="my-2 grid sm:grid-cols-2 gap-3">
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
     <div className="text-sm font-bold text-gray-900 dark:text-white mb-1">Spread <span className="text-amber-600 dark:text-amber-400">💥</span></div>
     <div className="text-[13px] text-gray-600 dark:text-gray-400">Expands / unpacks values out of an array or object.</div>
    </div>
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
     <div className="text-sm font-bold text-gray-900 dark:text-white mb-1">Rest <span className="text-amber-600 dark:text-amber-400">📦</span></div>
     <div className="text-[13px] text-gray-600 dark:text-gray-400">Collects / packs multiple values into one array or object.</div>
    </div>
   </div>
   <Callout tone="tip" title="Memory trick">Spread = explosion 💥. Rest = collection 📦.</Callout>
  </section>

  {/* 3. Why */}
  <section className="mb-12">
   <SectionHeading n={3}>Why They Exist</SectionHeading>
   <Para>Before ES6, merging (<Code>concat</Code>), copying (<Code>slice</Code>), and variable arguments (the clunky <Code>arguments</Code> object) were verbose and limited. <Code>...</Code> makes all of them concise and expressive.</Para>
  </section>

  {/* 4. Core idea */}
  <section className="mb-12">
   <SectionHeading n={4}>Core Idea — Two Personalities</SectionHeading>
   <Para>The same <Code>...</Code> behaves differently based on context:</Para>
   <div className="my-6 grid sm:grid-cols-2 gap-3">
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30 p-4">
     <div className="text-xs font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-2">Spread (expanding)</div>
     <pre className="text-[13px] font-mono text-gray-600 dark:text-gray-300">{`[1, 2, 3]  →  1, 2, 3`}</pre>
    </div>
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30 p-4">
     <div className="text-xs font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-2">Rest (collecting)</div>
     <pre className="text-[13px] font-mono text-gray-600 dark:text-gray-300">{`1, 2, 3  →  [1, 2, 3]`}</pre>
    </div>
   </div>
  </section>

  {/* 5. Spread with arrays */}
  <section className="mb-12">
   <SectionHeading n={5}>Spread with Arrays</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const fruits = ["Apple", "Banana"];\nconsole.log(...fruits); // unpacks -> "Apple" "Banana"` }]} defaultLanguage="javascript" id="sr-spread-arr" />
   <Output>Apple Banana</Output>
  </section>

  {/* 6. Merging arrays */}
  <section className="mb-12">
   <SectionHeading n={6}>Merging Arrays</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const fruits = ["Apple", "Banana"];\nconst vegetables = ["Potato", "Tomato"];\n\nconst items = [...fruits, ...vegetables];\n// ["Apple", "Banana", "Potato", "Tomato"]` }]} defaultLanguage="javascript" id="sr-merge-arr" />
  </section>

  {/* 7. Copying arrays */}
  <section className="mb-12">
   <SectionHeading n={7}>Copying Arrays</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const original = [1, 2, 3];\nconst copy = [...original];\n\ncopy.push(4);\n// original stays [1, 2, 3]; copy is [1, 2, 3, 4]` }]} defaultLanguage="javascript" id="sr-copy-arr" />
   <Callout tone="info">This independent-copy pattern is the backbone of immutability in React and Redux state updates.</Callout>
  </section>

  {/* 8. Shallow copy */}
  <section className="mb-12">
   <SectionHeading n={8}>Spread Is a Shallow Copy</SectionHeading>
   <Para>Spread copies only the <Mark>first level</Mark> — nested objects stay shared:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const users = [{ name: "Sachin" }];\nconst copy = [...users];\n\ncopy[0].name = "Rahul";\nconsole.log(users[0].name); // "Rahul" — nested object was shared` }]} defaultLanguage="javascript" id="sr-shallow" />
   <Callout tone="warning">For a fully independent clone use <Code>structuredClone()</Code>. This shallow-copy fact is a frequent interview question.</Callout>
  </section>

  {/* 9. Spread with objects */}
  <section className="mb-12">
   <SectionHeading n={9}>Spread with Objects</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = { name: "Sachin", city: "Bangalore" };\nconst copy = { ...user }; // { name: "Sachin", city: "Bangalore" }` }]} defaultLanguage="javascript" id="sr-spread-obj" />
  </section>

  {/* 10. Merging objects */}
  <section className="mb-12">
   <SectionHeading n={10}>Merging Objects</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = { name: "Sachin" };\nconst address = { city: "Bangalore" };\n\nconst profile = { ...user, ...address };\n// { name: "Sachin", city: "Bangalore" }` }]} defaultLanguage="javascript" id="sr-merge-obj" />
  </section>

  {/* 11. Override */}
  <section className="mb-12">
   <SectionHeading n={11}>Property Override — Last Value Wins</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = { name: "Sachin" };\nconst updated = { ...user, name: "Rahul" };\n// { name: "Rahul" }  — the later key overrides` }]} defaultLanguage="javascript" id="sr-override" />
   <Callout tone="info" title="Rule">When keys collide, the <Mark>last</Mark> one wins. This is exactly how you apply updates on top of an existing object.</Callout>
  </section>

  {/* 12. Spread in function calls */}
  <section className="mb-12">
   <SectionHeading n={12}>Spread in Function Calls</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const numbers = [10, 20, 30];\n\nMath.max(...numbers); // same as Math.max(10, 20, 30) -> 30\nMath.max(numbers);    // NaN — an array isn't individual args` }]} defaultLanguage="javascript" id="sr-call" />
   <Output>30</Output>
  </section>

  {/* 13. Rest intro + params */}
  <section className="mb-12">
   <SectionHeading n={13}>Rest Parameters</SectionHeading>
   <Para>Now the second personality. In a parameter list, <Code>...</Code> <Mark>collects</Mark> all arguments into a real array:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function sum(...numbers) {\n  return numbers.reduce((total, n) => total + n, 0);\n}\n\nsum(10, 20, 30); // numbers becomes [10, 20, 30] -> 60` }]} defaultLanguage="javascript" id="sr-rest-params" />
   <Output>60</Output>
  </section>

  {/* 14. Rest vs arguments */}
  <section className="mb-12">
   <SectionHeading n={14}>Why Rest Replaced arguments</SectionHeading>
   <Para>The old <Code>arguments</Code> object isn&apos;t a real array, has limited functionality, and reads poorly. Rest parameters give you a genuine array with all array methods available:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function test(...args) {\n  // args is a real array -> args.map, args.filter, etc.\n}` }]} defaultLanguage="javascript" id="sr-args" />
  </section>

  {/* 15. Rest with destructuring */}
  <section className="mb-12">
   <SectionHeading n={15}>Rest with Destructuring</SectionHeading>
   <Para>Grab the first item(s) and collect the rest:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const numbers = [1, 2, 3, 4, 5];\nconst [first, ...remaining] = numbers;\n// first -> 1, remaining -> [2, 3, 4, 5]` }]} defaultLanguage="javascript" id="sr-rest-destr" />
  </section>

  {/* 16. Object rest */}
  <section className="mb-12">
   <SectionHeading n={16}>Object Rest</SectionHeading>
   <Para>Pull out a key and collect everything else — extremely handy for API data:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = { id: 1, name: "Sachin", city: "Bangalore" };\nconst { name, ...others } = user;\n// name -> "Sachin", others -> { id: 1, city: "Bangalore" }` }]} defaultLanguage="javascript" id="sr-obj-rest" />
  </section>

  {/* 17. Spread vs rest */}
  <section className="mb-12">
   <SectionHeading n={17}>Spread vs Rest</SectionHeading>
   <div className="my-6 overflow-x-auto">
    <table className="w-full text-sm">
     <thead>
      <tr className="border-b border-gray-200 dark:border-gray-700">
       {['Feature', 'Spread', 'Rest'].map((h) => <th key={h} className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">{h}</th>)}
      </tr>
     </thead>
     <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
      {[
       ['Purpose', 'Expand values', 'Collect values'],
       ['Direction', 'Array → elements', 'Elements → array'],
       ['Used in', 'Arrays, objects, function calls', 'Function params, destructuring'],
       ['Mental model', 'Unpack', 'Pack'],
      ].map((r) => (
       <tr key={r[0]}>
        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{r[0]}</td>
        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{r[1]}</td>
        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{r[2]}</td>
       </tr>
      ))}
     </tbody>
    </table>
   </div>
   <Callout tone="info">Same syntax, opposite meaning — <Mark>context</Mark> decides. On the right side / in a call it spreads; in a parameter list or destructuring target it collects.</Callout>
  </section>

  {/* 18. React + Node */}
  <section className="mb-12">
   <SectionHeading n={18}>React &amp; Node.js Patterns</SectionHeading>
   <Para>Spread preserves existing React state while updating one field; object rest strips sensitive fields before sending a response:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// React: keep all fields, change one\nsetUser({ ...user, city: "Delhi" });\n\n// Node: drop the password before responding\nconst { password, ...safeUser } = user;\nreturn res.json(safeUser);` }]} defaultLanguage="javascript" id="sr-react-node" />
  </section>

  {/* 19. Across languages */}
  <section className="mb-12">
   <SectionHeading n={19}>Across Languages</SectionHeading>
   <CodeBlock snippets={[{ language: 'typescript', code: `function sum(...nums: number[]): number {\n  return nums.reduce((a, b) => a + b, 0);\n}` }]} defaultLanguage="typescript" id="sr-ts" />
   <CodeBlock snippets={[{ language: 'java', code: `public static int sum(int... nums) {\n  int total = 0;\n  for (int n : nums) total += n;\n  return total;\n}\n// sum(1, 2, 3);` }]} defaultLanguage="java" id="sr-java" />
   <Callout tone="info">Java&apos;s <Code>varargs</Code> (<Code>int... nums</Code>) is conceptually the same as JavaScript&apos;s rest parameters.</Callout>
  </section>

  {/* 20. Common mistakes */}
  <section className="mb-12">
   <SectionHeading n={20}>Common Mistakes</SectionHeading>
   <Callout tone="warning" title="Thinking spread is deep">It only copies the top level — nested objects stay shared.</Callout>
   <Callout tone="warning" title="Confusing spread and rest">Same <Code>...</Code>, opposite jobs — read the context.</Callout>
   <Callout tone="warning" title="Ignoring override order">In a merge, the last colliding key wins.</Callout>
   <Callout tone="warning" title="Rest not last">A rest parameter must be the final parameter — <Code>function test(...nums, x)</Code> is a syntax error.</Callout>
  </section>

  {/* 21. Performance */}
  <section className="mb-12">
   <SectionHeading n={21}>Performance Considerations</SectionHeading>
   <Para>Spread builds a <Mark>new</Mark> array/object each time. For huge datasets that&apos;s extra memory and allocation — usually fine, but worth knowing when copying very large structures in hot paths.</Para>
  </section>

  {/* 22. Real interview questions */}
  <section className="mb-12">
   <SectionHeading n={22}>Real Interview Questions</SectionHeading>
   <QA q="What is the difference between spread and rest?">
    Spread <em>expands</em> an array/object into individual values; rest <em>collects</em> individual values into one array/object. Same <Code>...</Code>, opposite directions.
   </QA>
   <QA q="Why is spread a shallow copy?">
    It copies only the top-level entries. Nested objects/arrays are copied by reference, so mutating a nested value affects both copies.
   </QA>
   <QA q="What happens when keys collide while merging objects?">
    The last value wins: <Code>{`{ ...user, name: "Rahul" }`}</Code> overrides any earlier <Code>name</Code>.
   </QA>
   <QA q="Why are rest parameters better than the arguments object?">
    Rest gives a real array (with map/filter/reduce), works with arrow functions, and reads more clearly. <Code>arguments</Code> is array-like only.
   </QA>
   <QA q="How do you remove a property immutably?">
    Object rest: <Code>const {`{ password, ...safeUser }`} = user</Code> leaves <Code>safeUser</Code> without <Code>password</Code>.
   </QA>
   <QA q="Where must a rest parameter appear?">
    Always last in the parameter list — only one rest parameter is allowed, and nothing can follow it.
   </QA>
  </section>

  {/* 23. Mental model */}
  <section className="mb-12">
   <SectionHeading n={23}>Mental Model</SectionHeading>
   <Para>Think of luggage. <Mark>Spread</Mark> opens the suitcase and lays everything out (clothes, shoes, laptop). <Mark>Rest</Mark> gathers loose items back into one suitcase. That&apos;s exactly how <Code>...</Code> behaves depending on context.</Para>
  </section>

  {/* 24 & 25 */}
  <section className="mb-4">
   <SectionHeading n={24}>Interview One-Liner &amp; Takeaway</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-2">Interview One-Liner</div>
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     The spread operator expands values out of arrays/objects, while the rest operator collects multiple values into a single array/object.
    </p>
   </div>
   <Callout tone="info" title="Most important takeaway">
    Spread and rest simplify copying, merging, destructuring, function parameters, React state updates, and API transformations. Remember the one distinction — <Mark>spread = expand, rest = collect</Mark> — and almost every related question becomes easy.
   </Callout>
  </section>
 </div>
);

export default JSSpreadRest;
